/**
 * Phase 5 PR-A — Governance → Classification sub-tab (v1).
 *
 * Per docs/SYSADMIN-PLATFORM-SPEC.md §3.5 + Phase 5 architect §2.1:
 *   - Flat table of classification taxonomy rules
 *     (Pattern | Sensitivity | Export Control | Priority | Auto-Apply | Actions)
 *   - New rule dialog (POST)
 *   - Delete via <TypedConfirmDialog> requiring the pattern typed back
 *   - Signal-word dictionary editor (ANSI Z535.6) read-only-by-default
 *     in v1; advanced editor lives in a side panel
 *
 * Built entirely on PR-0 canonical primitives.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Tags, Plus, Trash2, Edit2, Inbox, AlertTriangle, ShieldAlert,
  Save, X, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { SectionCard } from '@/components/ui/section-card';
import { StatusPill } from '@/components/ui/status-pill';
import { StatTile } from '@/components/ui/stat-tile';
import { EmptyState } from '@/components/ui/empty-state';
import { PageError } from '@/components/ui/page-error';
import { Toolbar } from '@/components/ui/toolbar';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import {
  useSysadminClassificationRules,
  useCreateSysadminClassificationRule,
  useDeleteSysadminClassificationRule,
  usePatchSysadminClassificationRule,
  useSysadminSignalWords,
  type ClassificationRule,
  type SignalWord,
} from '@/lib/use-sysadmin-governance';
import type { ExportControl, Sensitivity } from '@argusai/shared';

export const Route = createFileRoute('/sysadmin/governance/classification')({
  component: ClassificationPage,
});

// ─── Page ───────────────────────────────────────────────────────────────────

function ClassificationPage() {
  const [filterSensitivity, setFilterSensitivity] = useState<
    'all' | Sensitivity
  >('all');
  const [filterExport, setFilterExport] = useState<
    'all' | ExportControl
  >('all');
  const [showNew, setShowNew] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ClassificationRule | null>(
    null,
  );
  const [editTarget, setEditTarget] = useState<ClassificationRule | null>(null);

  const rulesQuery = useSysadminClassificationRules({
    sensitivity:
      filterSensitivity === 'all' ? undefined : filterSensitivity,
    exportControl: filterExport === 'all' ? undefined : filterExport,
    limit: 50,
  });
  const signalWordsQuery = useSysadminSignalWords();

  const create = useCreateSysadminClassificationRule();
  const remove = useDeleteSysadminClassificationRule();
  const patch = usePatchSysadminClassificationRule();

  const rules = rulesQuery.data?.items ?? [];
  const signalWords = signalWordsQuery.data?.items ?? [];

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    remove.mutate(
      { id: deleteTarget.id },
      { onSuccess: () => setDeleteTarget(null) },
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Tags className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Classification Taxonomy
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Document-type patterns mapped to (sensitivity, export control).
              The ingestion pipeline auto-classifies documents using the
              highest-priority matching rule.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => setShowNew(true)}
          title="Create a new classification rule"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          New rule
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Total rules"
          value={rules.length}
          tone="default"
          icon={Tags}
        />
        <StatTile
          label="Auto-apply"
          value={rules.filter((r) => r.autoApply).length}
          suffix={`/ ${rules.length}`}
          tone={rules.some((r) => r.autoApply) ? 'success' : 'muted'}
        />
        <StatTile
          label="ITAR rules"
          value={rules.filter((r) => r.exportControl === 'itar').length}
          tone={
            rules.some((r) => r.exportControl === 'itar')
              ? 'destructive'
              : 'muted'
          }
          icon={ShieldAlert}
        />
        <StatTile
          label="Signal words"
          value={signalWords.length}
          suffix="(ANSI Z535.6)"
          tone="warning"
          icon={AlertTriangle}
        />
      </div>

      {/* Toolbar / filters */}
      <Toolbar sticky={false}>
        <Select
          value={filterSensitivity}
          onValueChange={(v) =>
            setFilterSensitivity(v as typeof filterSensitivity)
          }
        >
          <SelectTrigger className="h-9 max-w-[180px] text-xs">
            <SelectValue placeholder="Sensitivity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sensitivities</SelectItem>
            <SelectItem value="unclassified">Unclassified</SelectItem>
            <SelectItem value="cui">CUI</SelectItem>
            <SelectItem value="restricted">Restricted</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filterExport}
          onValueChange={(v) => setFilterExport(v as typeof filterExport)}
        >
          <SelectTrigger className="h-9 max-w-[180px] text-xs">
            <SelectValue placeholder="Export control" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All export</SelectItem>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="itar">ITAR</SelectItem>
            <SelectItem value="ear">EAR</SelectItem>
          </SelectContent>
        </Select>
        <Toolbar.Right>
          <Button
            variant="outline"
            size="sm"
            onClick={() => rulesQuery.refetch()}
            disabled={rulesQuery.isFetching}
          >
            <RefreshCw
              className={
                rulesQuery.isFetching
                  ? 'h-3.5 w-3.5 mr-1 animate-spin'
                  : 'h-3.5 w-3.5 mr-1'
              }
            />
            Refresh
          </Button>
        </Toolbar.Right>
      </Toolbar>

      {/* Rules table */}
      {rulesQuery.error && (
        <PageError
          title="Failed to load classification rules"
          message={rulesQuery.error.message}
          onRetry={rulesQuery.refetch}
          retrying={rulesQuery.isFetching}
        />
      )}

      {!rulesQuery.error && (
        <>
          {rulesQuery.isLoading && rules.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
              Loading classification rules…
            </div>
          ) : rules.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title={
                filterSensitivity !== 'all' || filterExport !== 'all'
                  ? 'No rules match the current filters'
                  : 'No classification rules'
              }
              body={
                filterSensitivity !== 'all' || filterExport !== 'all'
                  ? 'Clear filters to see every rule.'
                  : 'Add a rule to start auto-classifying documents on ingestion.'
              }
              action={
                <Button size="sm" onClick={() => setShowNew(true)}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  New rule
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pattern</TableHead>
                  <TableHead>Sensitivity</TableHead>
                  <TableHead>Export</TableHead>
                  <TableHead>Auto-apply</TableHead>
                  <TableHead className="text-right">Priority</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">
                      {r.docTypePattern}
                    </TableCell>
                    <TableCell>
                      <StatusPill status={sensitivityTone(r.sensitivity)} compact>
                        {r.sensitivity.toUpperCase()}
                      </StatusPill>
                    </TableCell>
                    <TableCell>
                      <StatusPill status={exportTone(r.exportControl)} compact>
                        {r.exportControl.toUpperCase()}
                      </StatusPill>
                    </TableCell>
                    <TableCell>
                      {r.autoApply ? (
                        <StatusPill status="healthy" compact>
                          Yes
                        </StatusPill>
                      ) : (
                        <StatusPill status="muted" compact>
                          No
                        </StatusPill>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.priority}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">
                      {r.description ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditTarget(r)}
                          title="Edit this rule"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(r)}
                          title="Delete this rule"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {rulesQuery.data?.nextCursor && (
            <p className="text-xs text-muted-foreground py-2 text-center">
              More rules available. Narrow filters to drill in.
            </p>
          )}
        </>
      )}

      {/* Signal-words dictionary preview */}
      <SignalWordsPreview
        words={signalWords}
        loading={signalWordsQuery.isLoading}
        error={signalWordsQuery.error}
        onRefresh={signalWordsQuery.refetch}
      />

      {/* New / Edit dialog */}
      {(showNew || editTarget) && (
        <RuleEditorDialog
          mode={showNew ? 'create' : 'edit'}
          target={editTarget}
          isPending={create.isPending || patch.isPending}
          onClose={() => {
            setShowNew(false);
            setEditTarget(null);
          }}
          onSubmitCreate={(body) =>
            create.mutate(body, {
              onSuccess: () => setShowNew(false),
            })
          }
          onSubmitPatch={(id, body) =>
            patch.mutate(
              { id, body },
              {
                onSuccess: () => setEditTarget(null),
              },
            )
          }
          error={
            create.error?.message ?? patch.error?.message ?? null
          }
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <TypedConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          title="Delete classification rule"
          description="This will soft-delete the rule. The ingestion pipeline will stop applying it immediately. The row stays in the database for 90 days for audit recovery."
          consequences={[
            'Documents ingested after this point won\'t match the pattern',
            'Existing classifications on prior documents are not changed',
            'A platform_audit_logs row records the delete with reason',
            'The row is hard-deleted by the cleanup CRON after 90 days',
          ]}
          confirmPhrase={deleteTarget.docTypePattern}
          confirmLabel={remove.isPending ? 'Deleting…' : 'Delete rule'}
          requireReason
          reasonPlaceholder="Why is this rule being removed? (compliance review, superseded by a different pattern, ...)"
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
}

// ─── Signal words preview ──────────────────────────────────────────────────

function SignalWordsPreview({
  words,
  loading,
  error,
  onRefresh,
}: {
  words: SignalWord[];
  loading: boolean;
  error: Error | null;
  onRefresh: () => void;
}) {
  return (
    <SectionCard
      title="ANSI Z535.6 signal words"
      description="The chunker MUST NEVER separate a signal word from its procedure. The faithfulness gate verifies generated claims retain the source's signal word."
      icon={AlertTriangle}
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
        >
          <RefreshCw
            className={
              loading ? 'h-3.5 w-3.5 mr-1 animate-spin' : 'h-3.5 w-3.5 mr-1'
            }
          />
          Refresh
        </Button>
      }
    >
      {error ? (
        <PageError
          title="Failed to load signal words"
          message={error.message}
          onRetry={onRefresh}
          retrying={loading}
        />
      ) : loading && words.length === 0 ? (
        <div className="py-6 text-center text-muted-foreground text-sm animate-pulse">
          Loading signal words…
        </div>
      ) : words.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="Signal words dictionary not seeded"
          body="Migration bA15 seeds the 4 ANSI Z535.6 signal words."
          compact
        />
      ) : (
        <div className="space-y-2">
          {words.map((w) => (
            <div
              key={w.word}
              className="flex items-center gap-3 p-2 border rounded-md text-xs"
              style={{ borderLeftWidth: 4, borderLeftColor: w.color }}
            >
              <Badge
                variant="outline"
                className="font-mono uppercase"
                style={{ borderColor: w.color, color: w.color }}
              >
                {w.word}
              </Badge>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {w.severity}
              </span>
              <span className="flex-1">{w.definition}</span>
              {w.atomicWithProcedure && (
                <StatusPill status="warning" compact icon={AlertTriangle}>
                  Atomic with procedure
                </StatusPill>
              )}
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground italic">
            Edit-in-place editor ships in v2. PATCH endpoint is wired and
            audit-logged.
          </p>
        </div>
      )}
    </SectionCard>
  );
}

// ─── Rule editor dialog ────────────────────────────────────────────────────

interface RuleEditorDialogProps {
  mode: 'create' | 'edit';
  target: ClassificationRule | null;
  isPending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmitCreate: (body: {
    docTypePattern: string;
    sensitivity: Sensitivity;
    exportControl: ExportControl;
    autoApply: boolean;
    priority: number;
    description?: string;
  }) => void;
  onSubmitPatch: (
    id: string,
    body: Partial<{
      docTypePattern: string;
      sensitivity: Sensitivity;
      exportControl: ExportControl;
      autoApply: boolean;
      priority: number;
      description: string | null;
    }>,
  ) => void;
}

function RuleEditorDialog({
  mode, target, isPending, error,
  onClose, onSubmitCreate, onSubmitPatch,
}: RuleEditorDialogProps) {
  const [pattern, setPattern] = useState(target?.docTypePattern ?? '');
  const [sensitivity, setSensitivity] = useState<
    Sensitivity
  >(target?.sensitivity ?? 'unclassified');
  const [exportControl, setExportControl] = useState<
    ExportControl
  >(target?.exportControl ?? 'none');
  const [autoApply, setAutoApply] = useState(target?.autoApply ?? false);
  const [priority, setPriority] = useState(target?.priority ?? 0);
  const [description, setDescription] = useState(target?.description ?? '');

  const PATTERN_REGEX = /^[A-Za-z0-9_./\-*?]+$/;
  const patternValid = pattern.length > 0 && PATTERN_REGEX.test(pattern);
  const catchallAutoApply = pattern === '*' && autoApply;
  const canSubmit =
    patternValid && !catchallAutoApply && priority >= 0 && priority <= 1000;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (mode === 'create') {
      onSubmitCreate({
        docTypePattern: pattern,
        sensitivity,
        exportControl,
        autoApply,
        priority,
        description: description.trim() || undefined,
      });
    } else if (target) {
      onSubmitPatch(target.id, {
        docTypePattern: pattern,
        sensitivity,
        exportControl,
        autoApply,
        priority,
        description: description.trim() || null,
      });
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5 text-muted-foreground" />
            {mode === 'create'
              ? 'New classification rule'
              : 'Edit classification rule'}
          </DialogTitle>
          <DialogDescription>
            Maps a document-type pattern to a (sensitivity, export control)
            tuple. Higher priority wins when multiple rules match.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Pattern <span className="text-red-500">*</span>
            </Label>
            <Input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="MSDS-* | ITAR-* | *.dwg | LOTO-*"
              className="h-9 font-mono text-xs"
            />
            {pattern.length > 0 && !patternValid && (
              <p className="text-[10px] text-red-600">
                Pattern may contain letters, digits, _, ., /, -, *, and ?.
              </p>
            )}
            {catchallAutoApply && (
              <p className="text-[10px] text-red-600">
                A catch-all pattern (*) cannot have auto-apply enabled.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                Sensitivity <span className="text-red-500">*</span>
              </Label>
              <Select
                value={sensitivity}
                onValueChange={(v) => setSensitivity(v as typeof sensitivity)}
              >
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unclassified">Unclassified</SelectItem>
                  <SelectItem value="cui">CUI</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                Export control <span className="text-red-500">*</span>
              </Label>
              <Select
                value={exportControl}
                onValueChange={(v) =>
                  setExportControl(v as typeof exportControl)
                }
              >
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="itar">ITAR</SelectItem>
                  <SelectItem value="ear">EAR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Priority (0-1000)</Label>
              <Input
                type="number"
                min={0}
                max={1000}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="h-9 text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold flex items-center gap-2">
                <Switch
                  checked={autoApply}
                  onCheckedChange={setAutoApply}
                  id="auto-apply"
                />
                Auto-apply on ingestion
              </Label>
              <p className="text-[10px] text-muted-foreground">
                If off, flags for compliance officer review.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              className="h-9 text-xs"
              placeholder="Optional context for the next sysadmin reading this rule."
            />
          </div>

          {error && (
            <PageError
              title="Save failed"
              message={error}
              retrying={isPending}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            <X className="h-3.5 w-3.5 mr-1" />
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
            <Save className="h-3.5 w-3.5 mr-1" />
            {isPending
              ? 'Saving…'
              : mode === 'create'
                ? 'Create rule'
                : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Tone helpers ──────────────────────────────────────────────────────────

function sensitivityTone(
  s: Sensitivity,
): 'muted' | 'info' | 'destructive' {
  switch (s) {
    case 'unclassified':
      return 'muted';
    case 'cui':
      return 'info';
    case 'restricted':
      return 'destructive';
  }
}

function exportTone(
  e: ExportControl,
): 'muted' | 'warning' | 'destructive' {
  switch (e) {
    case 'none':
      return 'muted';
    case 'ear':
      return 'warning';
    case 'itar':
      return 'destructive';
  }
}
